import { writable } from 'svelte/store';
import type { ExerciseMode } from '../types/exercise';

interface DisabledMode {
  mode: ExerciseMode;
  disabledUntil: number; // timestamp
}

// Store for temporarily disabled exercise modes
const createDisabledModesStore = () => {
  const { subscribe, set, update } = writable<DisabledMode[]>([]);

  // Load from localStorage on init
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem('vocario_disabled_modes');
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as DisabledMode[];
        // Filter out expired modes
        const now = Date.now();
        const active = parsed.filter((dm) => dm.disabledUntil > now);
        set(active);
      } catch (e) {
        console.error('Failed to parse disabled modes from localStorage', e);
      }
    }
  }

  return {
    subscribe,

    /**
     * Disable a mode for specified duration (default 15 minutes)
     */
    disableMode: (mode: ExerciseMode, durationMs: number = 15 * 60 * 1000) => {
      const disabledUntil = Date.now() + durationMs;

      update((modes) => {
        // Remove existing entry for this mode if any
        const filtered = modes.filter((m) => m.mode !== mode);
        const newModes = [...filtered, { mode, disabledUntil }];

        // Save to localStorage
        if (typeof window !== 'undefined') {
          localStorage.setItem('vocario_disabled_modes', JSON.stringify(newModes));
        }

        return newModes;
      });
    },

    /**
     * Enable a mode immediately (remove from disabled list)
     */
    enableMode: (mode: ExerciseMode) => {
      update((modes) => {
        const newModes = modes.filter((m) => m.mode !== mode);

        // Save to localStorage
        if (typeof window !== 'undefined') {
          localStorage.setItem('vocario_disabled_modes', JSON.stringify(newModes));
        }

        return newModes;
      });
    },

    /**
     * Check if a mode is currently disabled
     */
    isDisabled: (mode: ExerciseMode): boolean => {
      let result = false;
      const now = Date.now();

      subscribe((modes) => {
        const found = modes.find((m) => m.mode === mode);
        result = found ? found.disabledUntil > now : false;
      })();

      return result;
    },

    /**
     * Get time remaining until mode is re-enabled (in milliseconds)
     * Returns 0 if mode is not disabled
     */
    getTimeRemaining: (mode: ExerciseMode): number => {
      let result = 0;
      const now = Date.now();

      subscribe((modes) => {
        const found = modes.find((m) => m.mode === mode);
        result = found ? Math.max(0, found.disabledUntil - now) : 0;
      })();

      return result;
    },

    /**
     * Clean up expired modes
     */
    cleanup: () => {
      const now = Date.now();
      update((modes) => {
        const active = modes.filter((m) => m.disabledUntil > now);

        // Save to localStorage
        if (typeof window !== 'undefined') {
          localStorage.setItem('vocario_disabled_modes', JSON.stringify(active));
        }

        return active;
      });
    },
  };
};

export const disabledExerciseModes = createDisabledModesStore();

// Cleanup expired modes every minute
if (typeof window !== 'undefined') {
  setInterval(() => {
    disabledExerciseModes.cleanup();
  }, 60 * 1000);
}
